import { GQLSearchLocations, searchLocations } from 'graphql/geospatial/queries';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { Locations, SelectedLocations } from '../types';
import { CACHE_30_DAYS } from 'utils/constants';
import { Location } from 'types/geospatial';

export type LocationDataState = {
  isStateListLoading: boolean;
  isCountyListLoading: boolean;
  isCoreAreaListLoading: boolean;
  isMetropolitanDivisionListLoading: boolean;
  isUrbanAreaListLoading: boolean;
  isZctaLoading: boolean;
};

type Params = {
  onSelectGeometry: (state: Location) => void;
  onNoResultFound: () => void;
};

export const useLocations = ({ onSelectGeometry, onNoResultFound }: Params) => {
  const [locations, setLocations] = useState<Locations>({
    state: [],
    county: [],
    core_area: [],
    metropolitan_division: [],
    urban_area: [],
    zcta: [],
  });
  const [selectedLocations, setSelectedLocations] = useState<SelectedLocations>({});
  const [zctaQuery, setZctaQuery] = useState<string>('');

  const selectedStateName = selectedLocations.state ? selectedLocations.state.name : '';

  const { isFetching: isStateListLoading } = useQuery<GQLSearchLocations>(
    ['state-list'],
    () =>
      searchLocations({
        kind: 'state',
        return: ['state'],
      }),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_30_DAYS,
      onSuccess: (data) => {
        setLocations((prevLocations) => {
          return {
            ...prevLocations,
            state: data.searchLocations.sort((locationA, locationB) =>
              locationA.name.localeCompare(locationB.name)
            ),
          };
        });

        const texasState = data.searchLocations.find(({ name }) => name === 'Texas');
        if (texasState) {
          setSelectedLocations((prevValue) => {
            const prevSelectedLocations = { ...prevValue };
            prevSelectedLocations.state = texasState;

            return prevSelectedLocations;
          });

          onSelectGeometry(texasState);
        }
      },
    }
  );

  const { isFetching: isCountyListLoading } = useQuery<GQLSearchLocations>(
    ['county-list', selectedStateName],
    () =>
      searchLocations({
        kind: 'state',
        name: selectedStateName,
        return: ['county'],
      }),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_30_DAYS,
      enabled: !!selectedStateName,
      onSuccess: (data) => {
        setLocations((prevLocations) => {
          return {
            ...prevLocations,
            county: data.searchLocations.sort((locationA, locationB) =>
              locationA.name.localeCompare(locationB.name)
            ),
          };
        });
      },
    }
  );

  const { isFetching: isCoreAreaListLoading } = useQuery<GQLSearchLocations>(
    ['core-area-list', selectedStateName],
    () =>
      searchLocations({
        kind: 'state',
        name: selectedStateName,
        return: ['core_area'],
      }),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_30_DAYS,
      enabled: !!selectedStateName,
      onSuccess: (data) => {
        setLocations((prevLocations) => {
          return {
            ...prevLocations,
            core_area: data.searchLocations.sort((locationA, locationB) =>
              locationA.name.localeCompare(locationB.name)
            ),
          };
        });
      },
    }
  );

  const { isFetching: isMetropolitanDivisionListLoading } = useQuery<GQLSearchLocations>(
    ['metropolitan-division-list', selectedStateName],
    () =>
      searchLocations({
        kind: 'state',
        name: selectedStateName,
        return: ['metropolitan_division'],
      }),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_30_DAYS,
      enabled: !!selectedStateName,
      onSuccess: (data) => {
        setLocations((prevLocations) => {
          return {
            ...prevLocations,
            metropolitan_division: data.searchLocations.sort((locationA, locationB) =>
              locationA.name.localeCompare(locationB.name)
            ),
          };
        });
      },
    }
  );

  const { isFetching: isUrbanAreaListLoading } = useQuery<GQLSearchLocations>(
    ['urban-area-list', selectedStateName],
    () =>
      searchLocations({
        kind: 'state',
        name: selectedStateName,
        return: ['urban_area'],
      }),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_30_DAYS,
      enabled: !!selectedStateName,
      onSuccess: (data) => {
        setLocations((prevLocations) => {
          return {
            ...prevLocations,
            urban_area: data.searchLocations.sort((locationA, locationB) =>
              locationA.name.localeCompare(locationB.name)
            ),
          };
        });
      },
    }
  );

  const { isFetching: isZctaLoading } = useQuery<GQLSearchLocations>(
    ['search-zcta-list', zctaQuery],
    () =>
      searchLocations({
        kind: 'zcta',
        name: zctaQuery,
        return: ['zcta'],
      }),
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_30_DAYS,
      enabled: zctaQuery !== '',
      onSuccess: (data) => {
        if (data.searchLocations.length) {
          const firstGeometryResult = data.searchLocations[0];

          setSelectedLocations((prevValue) => {
            const prevSelectedLocations = { ...prevValue };
            prevSelectedLocations.zcta = firstGeometryResult;

            return prevSelectedLocations;
          });

          setLocations((prevLocations) => {
            return {
              ...prevLocations,
              zcta: [firstGeometryResult],
            };
          });

          onSelectGeometry(firstGeometryResult);
        } else {
          onNoResultFound();
        }
      },
      onError: () => {
        onNoResultFound();
      },
    }
  );

  const locationDataState: LocationDataState = {
    isStateListLoading,
    isCountyListLoading,
    isCoreAreaListLoading,
    isMetropolitanDivisionListLoading,
    isUrbanAreaListLoading,
    isZctaLoading,
  };

  return {
    locations,
    selectedLocations,
    locationDataState,
    setSelectedLocations,
    zctaQuery,
    setZctaQuery,
  };
};
